import { Home, MessageSquare, Layers, Trophy, User } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useHaptic } from "@/hooks/useHaptic";

export function MobileBottomNav() {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  const haptic = useHaptic();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user) return;
    const fetchUnreadNotifications = async () => {
      const { count } = await supabase.from("notifications").select("*", { count: "exact", head: true }).eq("user_id", user.id).eq("is_read", false);
      setUnreadCount(count || 0);
    };
    fetchUnreadNotifications();
    const channel = supabase.channel("notifications_changes").on("postgres_changes", { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, () => { fetchUnreadNotifications(); }).subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  if (!isMobile) return null;

  const formatBadge = (n: number) => (n > 9 ? "9+" : String(n));

  const navItems = [
    { path: "/dashboard", icon: Home, label: t('mobileNav.home') },
    { path: "/chat", icon: MessageSquare, label: t('mobileNav.chat') },
    { path: "/flashcards", icon: Layers, label: t('mobileNav.cards') },
    { path: "/achievements", icon: Trophy, label: t('mobileNav.achievements') },
    { path: "/settings", icon: User, label: t('mobileNav.profile'), badge: unreadCount > 0 ? unreadCount : null },
  ];

  return (
    <nav
      aria-label={t('mobileNav.home')}
      className="fixed bottom-0 left-0 right-0 z-nav bg-card/98 backdrop-blur-lg border-t border-border shadow-[0_-2px_16px_-4px_hsl(204_71%_22%/0.08)] pb-safe-bottom"
    >
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              aria-current={isActive ? "page" : undefined}
              aria-label={item.label}
              onClick={() => haptic('light')}
              className={cn(
                "relative flex flex-col items-center justify-center gap-1 px-2 rounded-xl transition-all duration-200 min-h-[48px] min-w-[48px]",
                isActive ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground active:scale-95"
              )}
            >
              <div className="relative">
                <Icon className="h-5 w-5" />
                {item.badge && (
                  <Badge
                    variant="destructive"
                    className="absolute -top-2 -right-2 h-4 min-w-4 px-1 flex items-center justify-center text-[10px] leading-none"
                  >
                    {formatBadge(item.badge)}
                  </Badge>
                )}
              </div>
              <span className={cn("text-[10px] font-medium transition-all", isActive ? "opacity-100" : "opacity-70")}>{item.label}</span>
              {isActive && (<div className="absolute -top-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" aria-hidden="true" />)}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
